import React, { FC, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { PromptSectionDraft } from 'services/types/promptSection';

interface Props {
  close: () => void;
  promptSection: PromptSectionDraft;
  save?: (promptSection: PromptSectionDraft) => void;
}

const SimplePromptSectionModal: FC<Props> = ({ close, promptSection, save }) => {
  const [name, setName] = useState<string>(promptSection.name);
  const [displayName, setDisplayName] = useState<string>(promptSection.displayName);
  const [value, setValue] = useState<string>(promptSection.value);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value.replace(/[^a-zA-Z0-9]/g, '_'));
  };

  const handleSave = () => {
    if (save) {
      save({
        ...promptSection,
        name,
        displayName,
        value,
        permission: 'editable', // Always editable for simple view
      });
    }
    close();
  };

  const isFormValid = name.trim() && displayName.trim() && value.trim();

  return (
    <Modal open onClose={close}>
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '70%', md: '60%', lg: '50%' },
          maxHeight: '90vh',
          overflow: 'auto',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }}
      >
        <Stack spacing={3} p={4}>
          {/* Header with icon */}
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: '12px',
                backgroundColor: 'primary.main',
                color: 'white',
              }}
            >
              <AddIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="600" color="text.primary">
                Agregar Nueva Información
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Personaliza el conocimiento de tu agente
              </Typography>
            </Box>
          </Box>

          {/* Form fields */}
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" fontWeight="500" mb={1} color="text.primary">
                Identificador único
              </Typography>
              <TextField
                value={name}
                onChange={handleNameChange}
                placeholder="Ej: politicas_devolucion, horarios_atencion"
                fullWidth
                variant="outlined"
                disabled={!!promptSection.name}
                helperText="Solo letras, números y guiones bajos. No se puede cambiar después."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="500" mb={1} color="text.primary">
                Título descriptivo
              </Typography>
              <TextField
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Ej: Políticas de devolución, Horarios de atención"
                fullWidth
                variant="outlined"
                helperText="Un nombre claro que describa esta información"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="500" mb={1} color="text.primary">
                Contenido
              </Typography>
              <TextField
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Describe la información que quieres que tu agente conozca..."
                fullWidth
                multiline
                rows={6}
                variant="outlined"
                helperText="Esta información estará disponible para tu agente durante las conversaciones"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          </Stack>

          {/* Action buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} pt={2}>
            <Button
              onClick={close}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!isFormValid}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.3)',
              }}
            >
              Crear Información
            </Button>
          </Box>
        </Stack>
      </Paper>
    </Modal>
  );
};

SimplePromptSectionModal.defaultProps = {
  save: () => {},
};

export default SimplePromptSectionModal;
